import {
  ShopifyProduct,
  ShopifyMedia,
  ShopifyProductVariant,
  DroppsDiscountFormula,
  droppsCatalog,
  droppsProduct,
  PdpTabData,
  ScentDetails,
  Badge,
} from "@/store";
import { postJson } from "@/helpers/fetch";
import type {
  Image as ImageApi,
  Maybe as MaybeApi,
  Media as MediaApi,
  Metafield as MetafieldApi,
  MoneyV2 as MoneyV2Api,
  Product as ProductApi,
  ProductOption as ProductOptionApi,
  ProductVariant as ProductVariantApi,
  SelectedOption as SelectedOptionApi,
  SellingPlan as SellingPlanApi,
  PageInfo as PageInfoApi,
  VideoSource as VideoSourceApi,
  SellingPlanPriceAdjustmentValue as SellingPlanPriceAdjustmentValueApi,
  SellingPlanGroup as SellingPlanGroupApi,
} from "@shopify/hydrogen-react/storefront-api-types";
import { getNumber } from "@/helpers/validation";
import { Mass, validateUnit } from "@/helpers/Mass";
import { useQuery } from "@tanstack/react-query";
import { Money } from "@/helpers/Money";
import { check } from "@/helpers/utils";

type CollectionQueryParams = {
  limit?: number;
};

export const storefrontApiKeys = {
  all: ["storefront"] as const,
  collection: (collection: string, params?: CollectionQueryParams) => [
    ...storefrontApiKeys.all,
    collection,
    params,
  ],
  handle: (handle: string) => [...storefrontApiKeys.all, handle],
};

export const storefrontApi = {
  useCollection: (collection: string, params?: CollectionQueryParams) =>
    useQuery({
      queryKey: storefrontApiKeys.collection(collection, params),
      queryFn: () => fetchStoreFrontProducts(collection, params?.limit),
      select: (data) => droppsCatalog(data.map((x) => mapProduct(x))),
      staleTime: 30000,
    }),

  useIds: async (ids: string[]) => {
    const collectionProducts = await Promise.all(
      await fetchRelatedStoreFrontProducts(ids)
    );

    const products = collectionProducts.map((p) => mapProduct(p));
    return droppsCatalog(products);
  },

  // useQuery({
  //   queryFn: () => fetchRelatedStoreFrontProducts(ids),
  //   select: (data) => droppsCatalog(data.map((x) => mapProduct(x))),
  //   staleTime: 30000,
  // }),

  useAllProducts: () =>
    useQuery({
      queryKey: storefrontApiKeys.collection("all-dropps-products"),
      queryFn: async (): Promise<ShopifyProduct[]> => {
        const products: ProductGql[] = await fetchStoreFrontProducts(
          "all-dropps-products"
        );

        const productMap: Record<string, ProductGql> = products.reduce<
          Record<string, ProductGql>
        >((acc, p: ProductGql) => {
          acc[p.id] = p;
          return acc;
        }, {});
        const bundleMap = products.reduce<Record<string, ProductGql[]>>(
          (acc, p) => {
            if (p.bundledProducts && p.bundledProducts.value) {
              const bundledItems = JSON.parse(p.bundledProducts.value ?? "[]")
                .map((v: string) => {
                  return productMap[v];
                })
                .filter((p: ProductGql | undefined) => !!p) as ProductGql[];
              acc[p.id] = bundledItems;
            } else {
              acc[p.id] = [];
            }
            return acc;
          },
          {}
        );
        return products.map((p) => mapProduct(p, bundleMap[p.id]));
      },
      select: (data: ShopifyProduct[]) => droppsCatalog(data),
      staleTime: 30000,
    }),

  useHandle: (handle: string) =>
    useQuery({
      queryKey: storefrontApiKeys.handle(handle),
      queryFn: () => storefrontApi.handle(handle),
      select: (data) => droppsProduct(data),
      staleTime: 30000,
    }),

  handle: async (handle: string) =>
    storefrontApi.handles([handle]).then((x) => x[0]),

  useHandles: (handles: string[], key: string) =>
    useQuery({
      queryKey: storefrontApiKeys.handle(key),
      queryFn: () => storefrontApi.handles(handles),
      select: (data) => droppsCatalog(data),
      staleTime: 30000,
    }),

  handles: async (handles: string[]) => {
    const products = await getProducts("handle", handles);
    const bundles = await Promise.all(
      products.map((p) =>
        getProducts("id", JSON.parse(p?.bundledProducts?.value ?? "[]"))
      )
    );

    return products.map((p, i) => mapProduct(p, bundles[i]));
  },
};

type ImageGql = Pick<ImageApi, "url" | "width" | "height" | "altText">;

type MetafieldGql = Pick<MetafieldApi, "value" | "reference"> & {
  reference?: {
    type: string;
    fields: { key: string; value: string }[];
  };
};

type VideoSourceGql = Pick<
  VideoSourceApi,
  "url" | "mimeType" | "format" | "height" | "width"
>;

type SellingPlanGql = Pick<
  SellingPlanApi,
  "id" | "name" | "description" | "priceAdjustments"
>;

type MediaImageGql = {
  previewImage: ImageGql;
};

type MediaVideoGql = {
  previewImage: ImageGql;
  sources: VideoSourceGql[];
};

type MediaGql = Pick<MediaApi, "alt"> &
  (
    | (MediaImageGql & { mediaContentType: "IMAGE" })
    | (MediaVideoGql & { mediaContentType: "VIDEO" })
  );

type MoneyV2Gql = Pick<MoneyV2Api, "amount" | "currencyCode">;

type ProductVariantGql = Pick<
  ProductVariantApi,
  | "id"
  | "title"
  | "sku"
  | "quantityAvailable"
  | "requiresShipping"
  | "availableForSale"
  | "weight"
  | "weightUnit"
  | "barcode"
> & {
  selectedOptions: Pick<SelectedOptionApi, "name" | "value">[];
  compareAtPriceV2: MaybeApi<MoneyV2Gql>;
  priceV2: MoneyV2Gql;
  hidden?: MetafieldGql;
  package_quantity?: MetafieldGql;
  default_variant_multiplier?: MetafieldGql;
  tiered_discount_variant_multiplier?: MetafieldGql;
  quantity_discount?: MetafieldGql;
  hide_subscription_button?: MetafieldGql;
  override_title?: MetafieldGql;
};

type SellingPlanGroupGql = Pick<SellingPlanGroupApi, "name"> & {
  sellingPlans: {
    nodes: SellingPlanGql[];
  };
};

type ProductGql = Pick<
  ProductApi,
  | "id"
  | "title"
  | "tags"
  | "descriptionHtml"
  | "productType"
  | "handle"
  | "requiresSellingPlan"
> & {
  options: Pick<ProductOptionApi, "name" | "values">[];
  badgeImage?: MetafieldGql;
  badgePosition?: MetafieldGql;
  badgeWidth?: MetafieldGql;
  badgeText?: MetafieldGql;
  collections: {
    nodes: {
      id: string;
      handle: string;
      title: string;
    }[];
  };
  frontBoxImage?: MetafieldGql;
  included?: MetafieldGql;
  bundledProducts?: MetafieldGql;
  featuredQuote?: MetafieldGql;
  relatedProducts?: MetafieldGql;
  hidden?: MetafieldGql;
  hideSubscription?: MetafieldGql;
  overrideFutureShipments?: MetafieldGql;
  overrideSpotPrice?: MetafieldGql;
  productIngredientDisclosureUrl?: MetafieldGql;
  ingredientSectionDrawerData?: MetafieldGql;
  overviewTabData?: MetafieldGql;
  impactTabData?: MetafieldGql;
  ingredientsTabData?: MetafieldGql;
  productShortText?: MetafieldGql;
  scent?: MetafieldGql;
  productGroup?: MetafieldGql;
  productCardDescription?: MetafieldGql;
  relatedProductImage?: MetafieldGql;
  media: {
    nodes: MediaGql[];
  };
  variants: {
    nodes: ProductVariantGql[];
  };
  sellingPlanGroups: {
    nodes: SellingPlanGroupGql[];
  };
};

const shopifyPriceV2ToPrice = (price: MoneyV2Gql | null) => {
  const p = getNumber(price?.amount);
  return p ? Money.from(p, "$") : null;
};

/// Turns "gid://shopify/ProductVariant/37970405074" into "37970405074"
const stripShopifyIdPrefix = (x: string) => getNumber(x.split("/").pop() ?? x);

function mapDiscount(
  x: SellingPlanPriceAdjustmentValueApi
): DroppsDiscountFormula {
  switch (x?.__typename) {
    case "SellingPlanFixedAmountPriceAdjustment": {
      const offset = Money.from(+x.adjustmentAmount.amount, "$");
      return {
        kind: "fixed",
        offset,
      };
    }
    case "SellingPlanFixedPriceAdjustment": {
      return {
        kind: "price",
        price: Money.from(+x.price.amount, "$"),
      };
    }
    case "SellingPlanPercentagePriceAdjustment": {
      return {
        kind: "percentage",
        percentage: x.adjustmentPercentage,
      };
    }
    default:
      throw new Error(`unexpected storefront discount ${x}`);
  }
}

const mapVariant = (variant: ProductVariantGql): ShopifyProductVariant => {
  const {
    id: stringId,
    weight,
    weightUnit,
    title,
    selectedOptions,
    sku,
    quantityAvailable: inventory_quantity,
    availableForSale: available,
    requiresShipping: requires_shipping,
    priceV2,
    compareAtPriceV2,
    barcode,
    hidden,
    package_quantity,
    default_variant_multiplier,
    tiered_discount_variant_multiplier,
    quantity_discount,
    hide_subscription_button,
    override_title,
  } = variant;

  const id = stripShopifyIdPrefix(stringId) ?? 0;
  const mass = Mass.from(weight ?? 0, validateUnit(weightUnit ?? "g"));

  return {
    id,
    title,
    sku: sku ?? null,
    requires_shipping,
    available,
    inventory_quantity: inventory_quantity ?? 0,
    options: selectedOptions.map((o) => o.value),
    price: shopifyPriceV2ToPrice(priceV2) ?? Money.zero,
    weight: mass,
    compare_at_price: shopifyPriceV2ToPrice(compareAtPriceV2),
    barcode: barcode ?? null,
    hidden: hidden?.value === "true",
    package_quantity: parseInt(package_quantity?.value ?? "64"),
    variant_multiplier: parseInt(default_variant_multiplier?.value ?? "1"),
    tiered_discount_variant_multiplier:
      tiered_discount_variant_multiplier?.value
        ? parseInt(tiered_discount_variant_multiplier.value)
        : null,
    quantity_discount: parseInt(quantity_discount?.value ?? "0"),
    hide_subscription_button: hide_subscription_button?.value === "true",
    override_title: override_title?.value ?? undefined,
  };
};

const mapMedia = (node: MediaGql): ShopifyMedia => {
  const { alt, mediaContentType, previewImage } = node;

  if (!previewImage.width || !previewImage.height) {
    throw new Error("preview image not hosted on shopify");
  }

  if (mediaContentType === "VIDEO") {
    return {
      ...previewImage,
      width: previewImage.width,
      height: previewImage.height,
      alt: alt ?? null,
      mediaType: "video",
      sources: node.sources.map(({ url, ...rest }) => ({
        ...rest,
        videoUrl: url,
      })),
    };
  } else {
    return {
      ...previewImage,
      width: previewImage.width,
      height: previewImage.height,
      alt: alt ?? null,
      mediaType: "image",
      altText: previewImage.altText ?? "",
    };
  }
};

const mapProduct = (
  node: ProductGql,
  bundledNode?: ProductGql[]
): ShopifyProduct => {
  const {
    id,
    tags,
    title,
    descriptionHtml,
    options,
    productType,
    handle,
    badgeImage,
    badgePosition,
    badgeWidth,
    badgeText,
    collections,
    frontBoxImage,
    media,
    variants: variantsGql,
    included,
    featuredQuote,
    relatedProducts,
    hidden,
    hideSubscription,
    overrideFutureShipments,
    overrideSpotPrice,
    productIngredientDisclosureUrl,
    ingredientSectionDrawerData,
    overviewTabData,
    impactTabData,
    ingredientsTabData,
    scent,
    productGroup,
    productCardDescription,
    productShortText,
    relatedProductImage,
  } = node;

  let scentData: ScentDetails | undefined;

  if (scent && scent.reference && scent.reference.type === "scent") {
    const fields = scent.reference.fields.reduce((acc, field) => {
      acc[field.key] = field.value;
      return acc;
    }, {} as { [key: string]: string });

    scentData = {
      title: fields.name,
      name: fields.name,
      description: fields.description,
      icon: fields.scent_icon,
      color: fields.color,
      handle: handle, // Add if available
      hidden: false, // Adjust based on your data
      pdpFragranceSectionContent: null,
      pdpFragranceSectionImage: null,
      isFragnanceSectionEnable: false,
    };
  }

  const pdpIngredientSection = ingredientSectionDrawerData?.value
    ? {
        ingredientDisclosureUrl: productIngredientDisclosureUrl?.value || "",
        ingredientSectionDrawerData:
          ingredientSectionDrawerData?.value &&
          JSON.parse(ingredientSectionDrawerData?.value),
      }
    : undefined;
  const pdpTabData: PdpTabData = {
    overview: overviewTabData?.value || "",
    impact: impactTabData?.value || "",
    ingredients: ingredientsTabData?.value || "",
  };
  const itemDescriptions: string[] = JSON.parse(included?.value || "[]");
  const bundledItems = {
    bundledSubscriptionProducts: (bundledNode || []).map((node) =>
      mapProduct(node)
    ),
    itemDescriptions,
  };

  // This currently assumes that selling plans are ordered by duration
  // and can be indexed by the interval selection.
  const sellingPlanNodes = node.sellingPlanGroups.nodes.flatMap(
    (x) => x.sellingPlans.nodes
  );

  const sellingPlans = sellingPlanNodes.map((x) => {
    const adjustment = x.priceAdjustments[0]?.adjustmentValue;
    const discount = adjustment
      ? mapDiscount(adjustment)
      : ({ kind: "percentage", percentage: 0 } as const);

    const id = check(
      stripShopifyIdPrefix(x.id),
      "selling plan id is not a number"
    );
    return {
      id,
      name: x.name,
      discount,
    };
  });

  const variants = variantsGql.nodes.map(mapVariant).map((v) => {
    const newPrice =
      overrideSpotPrice &&
      getNumber(JSON.parse(overrideSpotPrice.value).amount);
    v.price = Money.from(newPrice || v.price.asDollars(), "$");
    return v;
  });

  let parsedRelatedProducts: any[] = [];
  try {
    if (relatedProducts?.value) {
      parsedRelatedProducts = JSON.parse(relatedProducts?.value ?? "") || [];
    }
  } catch (error) {
    console.error("Error parsing relatedProducts:", error);
  }

  const badgeData: Badge | undefined =
    badgeImage?.reference ||
    badgePosition?.value ||
    badgeWidth?.value ||
    badgeText?.value
      ? {
          // @ts-ignore
          image:
            badgeImage?.reference && "url" in badgeImage.reference
              ? badgeImage.reference.url ?? undefined
              : undefined,
          position:
            (badgePosition?.value as "top-right" | "bottom-right") ||
            "top-right",
          width: badgeWidth?.value ? parseInt(badgeWidth.value) : 30,
          text: badgeText?.value,
        }
      : undefined;

  return {
    id: stripShopifyIdPrefix(id) ?? 0,
    tags,
    title,
    descriptionHtml,
    bundledItems:
      itemDescriptions.length || bundledNode?.length ? bundledItems : undefined,
    options: options.map((o) => o.name),
    type: productType,
    handle,
    badge: { ...badgeData },
    collections: collections.nodes.map((c) => ({
      id: c.id,
      handle: c.handle,
      title: c.title,
    })),
    frontBoxImage: frontBoxImage?.value || undefined,
    media: media.nodes.map(mapMedia),
    variants,
    featuredQuote: featuredQuote?.value || undefined,
    relatedProducts: parsedRelatedProducts,
    hidden: hidden?.value === "true",
    sellingPlans,
    hideSubscription: hideSubscription?.value === "true",
    overrideFutureShipments: overrideFutureShipments?.value,
    pdpIngredientSection,
    pdpTabData,
    scent: scentData,
    productGroup: productGroup?.value,
    productCardDescription: productCardDescription?.value,
    productShortText: productShortText?.value ?? "",
    recommendedImage:
      relatedProductImage?.reference && "image" in relatedProductImage.reference
        ? relatedProductImage.reference.image?.url ?? null
        : null,
  };
};

const graphqlProductFields = `
id
title
descriptionHtml
tags
options {
  name
  values
}
productType
handle
badgeText: metafield(namespace:"global" key:"badge") {
  value
}
badgeImage: metafield(namespace:"global" key:"badge_image") {
  reference {
    ... on MediaImage {
      image {
        url
      }
    }
    ... on GenericFile {
      url
    }
  }
}
badgePosition: metafield(namespace:"global" key:"badge_position") {
  value
}
badgeWidth: metafield(namespace:"global" key:"badge_width") {
  value
}
collections(first: 100) {
  nodes {
    id
    handle
    title
  }
}
frontBoxImage: metafield(namespace:"custom", key:"front_box_image") {
  value
}
bundledProducts: metafield(namespace:"global" key:"bundled_products") {
  value
}
included: metafield(namespace:"global" key:"included") {
  value
}
featuredQuote: metafield(namespace:"global" key:"featured_quote") {
  value
}
relatedProducts: metafield(namespace:"global" key:"related_products") {
  value
}
hidden: metafield(namespace:"global", key:"hide_product") {
  value
}
hideSubscription: metafield(namespace:"global", key:"hide_subscription") {
  value
}
overrideSpotPrice: metafield(namespace:"global", key:"override_spot_price") {
  value
}
overrideFutureShipments: metafield(namespace:"global", key:"override_future_shipments_section") {
  value
}
productIngredientDisclosureUrl: metafield(namespace:"global", key:"disclosure") {
  value
}
ingredientSectionDrawerData: metafield(namespace:"ingredient_section", key:"drawer_data") {
  value
}
overviewTabData: metafield(namespace:"tab_data", key:"overview") {
  value
}
impactTabData: metafield(namespace:"tab_data", key:"impact") {
  value
}
ingredientsTabData: metafield(namespace:"tab_data", key:"ingredients") {
  value
}
scent: metafield(namespace: "custom", key: "scent") {
  value
  reference {
    ... on Metaobject {
      type
      fields {
        key
        value
      }
    }
  }
}
productGroup: metafield(namespace:"custom", key:"product_group") {
  value
}
productCardDescription: metafield(namespace:"custom", key:"product_card_description") {
  value
}
productShortText: metafield(namespace:"global", key:"product_short_text") {
  value
}
relatedProductImage: metafield(namespace:"custom", key:"related_product_image") {
  reference {
    ... on MediaImage {
      image {
        url
      }
    }
  }
}
media(first:10){
  nodes {
    mediaContentType
    previewImage {
    url
    width
    height
    altText
    }
    ... on Video {
      sources {
        url
        mimeType
        format
        height
        width
      }
    }
  }
}
variants(first:10){
  nodes{
    id
    title
    selectedOptions {
      name
      value
    }
    sku
    quantityAvailable
    requiresShipping
    image {
      url
      width
      height
      altText
    }
    availableForSale
    title
    weight
    weightUnit
    compareAtPriceV2 {
      amount
      currencyCode
    }
    priceV2 {
      amount
      currencyCode
    }
    barcode
    hidden: metafield(namespace: "variant", key: "hide"){
      value
    }
    package_quantity: metafield(namespace:"custom", key:"package_quantity") {
      value
    }
    default_variant_multiplier: metafield(namespace:"custom", key:"default_variant_multiplier") {
      value
    }
    tiered_discount_variant_multiplier: metafield(namespace:"custom", key:"tiered_discount_variant_multiplier") {
      value
    }
    quantity_discount: metafield(namespace:"custom", key:"quantity_discount") {
      value
    }
    hide_subscription_button: metafield(namespace:"custom", key:"hide_subscription_button") {
      value
    }
    override_title: metafield(namespace:"custom", key:"override_title") {
      value
    }
  }
}

requiresSellingPlan
sellingPlanGroups(first:10) {
  nodes {
    name
    sellingPlans(first: 10) {
      nodes {
        id
        name
        priceAdjustments {
          adjustmentValue {
            __typename 
            ... on SellingPlanFixedAmountPriceAdjustment {
              adjustmentAmount {
                amount
                currencyCode
              }
            }
            ... on SellingPlanFixedPriceAdjustment {
              price {
                amount
                currencyCode
              }
            }
            ... on SellingPlanPercentagePriceAdjustment {
              adjustmentPercentage
            }
          }
        }
      }
    }
  }
}
`;

const productQuery = (query: string, alias?: string) => {
  return `${alias ? `${alias}: ` : ""}product(${query}) {
    ${graphqlProductFields}
  }`;
};

const collectionQuery = (
  collectionHandle: string,
  cursor?: string | null,
  batchSize?: number
) => {
  return `
    collection(handle:"${collectionHandle}"){
      ${productsQuery(cursor, batchSize)}
    }
  `;
};

const productsQuery = (cursor?: string | null, batchSize: number = 250) => {
  return `
    products(${cursor ? `after:"${cursor}"` : ""} ${`first: ${batchSize}`}){
      pageInfo {
        endCursor
        hasNextPage
      }
      nodes {
        ${graphqlProductFields}
      }
    }
  `;
};

const relatedProductsQuery = (ids: string[]) => {
  return `
  {
    nodes(ids: ${JSON.stringify(ids)}) {
      ... on Product {
        ${graphqlProductFields}
      }
    }
  }
  `;
};

const getProducts = async (
  key: string,
  values: string[] | number[]
): Promise<ProductGql[]> => {
  if (values.length === 0) {
    return Promise.resolve([]);
  }

  const queryContent = values
    .map((value, idx) => productQuery(`${key}:"${value}"`, `p${idx}`))
    .join(",");
  return queryStorefrontApi<any>(`{${queryContent}}`).then((x) =>
    values.map((v, idx) => x.data[`p${idx}`] as ProductGql)
  );
};

const fetchStoreFrontProducts = async (
  collectionHandle?: string,
  numProducts?: number
): Promise<ProductGql[]> => {
  const allProducts: ProductGql[] = [];
  let hasNextPage = true;
  let endCursor = undefined;
  const batch = numProducts && numProducts < 250 ? numProducts : 250;
  do {
    const query = collectionHandle
      ? `{${collectionQuery(collectionHandle, endCursor, batch)}}`
      : `{${productsQuery(endCursor, batch)}}`;
    const result = await queryStorefrontApi<any>(query);
    const root = collectionHandle
      ? result.data.collection.products
      : result.data.products;
    const pageInfo: PageInfoApi = root.pageInfo;
    hasNextPage = pageInfo.hasNextPage;
    endCursor = pageInfo.endCursor;
    allProducts.push(...root.nodes);
  } while (
    hasNextPage &&
    endCursor &&
    (!numProducts || allProducts.length < numProducts)
  );
  return allProducts;
};

const fetchRelatedStoreFrontProducts = async (
  ids: string[]
): Promise<ProductGql[]> => {
  const allProducts: ProductGql[] = [];
  const numProducts = ids.length;

  do {
    const query = relatedProductsQuery(ids);
    const result = await queryStorefrontApi<any>(query);
    const root = result.data.nodes;
    allProducts.push(...root);
  } while (!numProducts || allProducts.length < numProducts);
  return allProducts;
};

export const queryStorefrontApi = <T = unknown>(query: string) => {
  // This is a generic function to query the Shopify Storefront API
  // Using full absolute URL, per shopify recommendations: https://shopify.dev/api/storefront#endpoints
  return postJson<T>("https://dropps.myshopify.com/api/2025-01/graphql.json", {
    headers: {
      "X-Shopify-Storefront-Access-Token": "cc7235a888391c04d9671d91577da5c3",
    },
    body: {
      query,
      operationName: null,
      variables: null,
    },
  });
};
